import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import JsonView from '@uiw/react-json-view'
import { useAuth } from '@/lib/auth/useAuth'
import { apiFetch, apiJson, orgPath } from '@/lib/api/client'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ChevronDown,
  ChevronRight,
  Database,
  Download,
  Loader2,
  Shield,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react'
import { toast } from 'sonner'
import type {
  AuditListPage,
  AuditRow,
  AuditVerifyResult,
  MirrorStatus,
  MirrorStatusPage,
} from '@/lib/types/audit'
import { ACTION_GROUPS } from '@/lib/types/audit'

export type AuditScope = 'org' | 'platform'

interface Endpoints {
  list: string
  verify: string
  exportBase: string
}

function endpointsFor(scope: AuditScope, filters: URLSearchParams): Endpoints {
  if (scope === 'platform') {
    return {
      list: `/api/admin/audit?${filters}`,
      verify: '/api/admin/audit/verify',
      exportBase: '/api/admin/audit/export',
    }
  }
  return {
    list: orgPath(`/admin/audit?${filters}`),
    verify: orgPath('/admin/audit/verify'),
    exportBase: orgPath('/admin/audit/export'),
  }
}

export function AuditPage({ scope }: { scope: AuditScope }) {
  const { currentOrgSlug } = useAuth()
  const [actionGroup, setActionGroup] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [cursor, setCursor] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const filters = useMemo(() => {
    const params = new URLSearchParams()
    if (search.trim()) params.set('search', search.trim())
    if (cursor !== null) params.set('cursor', String(cursor))
    if (actionGroup !== 'all') {
      const actions = ACTION_GROUPS[actionGroup] ?? []
      if (actions.length === 1) {
        params.set('action', actions[0]!)
      }
    }
    params.set('limit', '100')
    return params
  }, [search, cursor, actionGroup])

  const endpoints = endpointsFor(scope, filters)
  const enabled = scope === 'platform' || !!currentOrgSlug

  const queryKey =
    scope === 'platform'
      ? ['audit-events', 'platform', filters.toString()]
      : ['audit-events', 'org', currentOrgSlug ?? '', filters.toString()]

  const query = useQuery<AuditListPage, Error>({
    queryKey,
    queryFn: () => apiJson<AuditListPage>(endpoints.list),
    enabled,
  })

  const groupActionSet = useMemo(() => {
    if (actionGroup === 'all') return null
    return new Set(ACTION_GROUPS[actionGroup] ?? [])
  }, [actionGroup])

  const events = useMemo(() => {
    const fetched = query.data?.events ?? []
    if (!groupActionSet) return fetched
    return fetched.filter((e) => groupActionSet.has(e.action))
  }, [query.data, groupActionSet])

  const handleExport = (format: 'jsonl' | 'csv') => {
    const params = new URLSearchParams()
    params.set('format', format)
    if (actionGroup !== 'all') {
      const actions = ACTION_GROUPS[actionGroup] ?? []
      if (actions.length === 1) params.set('action', actions[0]!)
    }
    apiFetch(`${endpoints.exportBase}?${params}`)
      .then(async (r) => {
        if (!r.ok) {
          toast.error(`Export failed (${r.status})`)
          return
        }
        const blob = await r.blob()
        triggerDownload(blob, `audit-events.${format}`)
      })
      .catch((e) => toast.error(`Export failed: ${e.message}`))
  }

  const title = scope === 'platform' ? 'Platform audit log' : 'Audit log'
  const description =
    scope === 'platform'
      ? 'Tamper-evident record of every action across every organization. Includes platform-level events (org lifecycle, system settings, audit infrastructure) that organization admins do not see.'
      : 'Tamper-evident record of every action in this organization. Each row links to the previous via SHA-256.'

  return (
    <div className="px-10 py-8">
      <PageHeader
        title={title}
        description={description}
        right={
          <>
            <ChainStatusPill verifyUrl={endpoints.verify} enabled={enabled} />
            <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
              <Download className="size-3.5" strokeWidth={1.75} />
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('jsonl')}>
              <Download className="size-3.5" strokeWidth={1.75} />
              JSONL
            </Button>
          </>
        }
      />

      {scope === 'platform' && <MirrorStatusStrip />}

      <div className="mb-6 flex gap-3 items-center flex-wrap">
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setCursor(null)
          }}
          placeholder="action, actor, resource..."
          className="max-w-sm bg-secondary border-transparent font-mono text-xs"
        />
        <Select
          value={actionGroup}
          onValueChange={(v) => {
            setActionGroup(v)
            setCursor(null)
          }}
        >
          <SelectTrigger className="h-9 text-xs w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {Object.keys(ACTION_GROUPS).map((g) => (
              <SelectItem key={g} value={g}>
                {g}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {cursor !== null && (
          <Button variant="ghost" size="sm" onClick={() => setCursor(null)}>
            Reset to latest
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <div className="border border-border bg-card/30 rounded-md h-[240px] hawk-shimmer" />
      ) : events.length === 0 ? (
        <div className="border border-border border-dashed rounded-md py-16 text-center bg-card/30">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
            No events
          </div>
          <p className="text-sm text-muted-foreground">
            Nothing recorded yet for the current filter.
          </p>
        </div>
      ) : (
        <ul className="border border-border rounded-md bg-card divide-y divide-border">
          {events.map((e) => (
            <AuditRowItem
              key={e.id}
              event={e}
              expanded={expanded === e.id}
              onToggle={() => setExpanded(expanded === e.id ? null : e.id)}
              showOrgColumn={scope === 'platform'}
              showMirrorAcks={scope === 'platform'}
            />
          ))}
        </ul>
      )}

      {query.data?.next_cursor !== null && query.data?.next_cursor !== undefined && (
        <div className="mt-6 flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor(query.data!.next_cursor)}
          >
            Load older
          </Button>
        </div>
      )}
    </div>
  )
}

function MirrorStatusStrip() {
  const { data } = useQuery({
    queryKey: ['audit-mirrors-strip'],
    queryFn: () => apiJson<MirrorStatusPage>('/api/admin/audit/mirrors'),
  })

  const mirrors = data?.mirrors ?? []
  if (mirrors.length === 0) return null

  return (
    <Link
      to="/admin/audit/mirrors"
      className="mb-5 flex items-center gap-2 flex-wrap text-xs no-underline"
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mr-1">
        Mirrors
      </span>
      {mirrors.map((m) => (
        <MirrorChip key={m.id} mirror={m} />
      ))}
    </Link>
  )
}

function MirrorChip({ mirror }: { mirror: MirrorStatus }) {
  const tone = mirror.healthy
    ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300'
    : 'border-yellow-500/30 bg-yellow-500/5 text-yellow-200'
  const dot = mirror.healthy ? 'bg-emerald-400' : 'bg-yellow-300'
  const lagLabel = mirror.lag > 0 ? ` / lag ${mirror.lag}` : ''
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${tone}`}
    >
      <span className={`size-1.5 rounded-full ${dot}`} />
      <Database className="size-3" strokeWidth={1.75} />
      <span className="text-foreground">{mirror.id}</span>
      <span className="text-muted-foreground">{lagLabel}</span>
    </span>
  )
}

function AuditRowItem({
  event,
  expanded,
  onToggle,
  showOrgColumn,
  showMirrorAcks,
}: {
  event: AuditRow
  expanded: boolean
  onToggle: () => void
  showOrgColumn: boolean
  showMirrorAcks: boolean
}) {
  return (
    <li className="hover:bg-popover/60 transition-colors">
      <button
        type="button"
        onClick={onToggle}
        className="w-full grid grid-cols-[16px_44px_minmax(280px,1fr)_minmax(160px,260px)_90px] gap-4 items-center px-5 py-3 text-left"
      >
        {expanded ? (
          <ChevronDown className="size-3.5 text-muted-foreground" strokeWidth={1.75} />
        ) : (
          <ChevronRight className="size-3.5 text-muted-foreground" strokeWidth={1.75} />
        )}
        <span className="font-mono text-[10px] text-muted-foreground/60 tabular-nums">
          #{event.seq}
        </span>
        <span className="text-sm font-medium truncate font-mono text-foreground">
          {event.action}
        </span>
        <ResourceCell event={event} />
        <span
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground tabular-nums text-right"
          title={new Date(event.created_at).toLocaleString()}
        >
          {formatDate(event.created_at)}
        </span>
      </button>
      {expanded && (
        <ExpandedRow
          event={event}
          showOrgColumn={showOrgColumn}
          showMirrorAcks={showMirrorAcks}
        />
      )}
    </li>
  )
}

function ResourceCell({ event }: { event: AuditRow }) {
  if (!event.resource_type) return <span />
  return (
    <span
      className="font-mono text-[10px] truncate"
      title={
        event.resource_id
          ? `${event.resource_type} ${event.resource_id}`
          : event.resource_type
      }
    >
      <span className="text-muted-foreground/50">{event.resource_type}</span>
      {event.resource_id && (
        <>
          <span className="text-muted-foreground/30 px-1.5">/</span>
          <span className="text-muted-foreground/85">{shortId(event.resource_id)}</span>
        </>
      )}
    </span>
  )
}

function ExpandedRow({
  event,
  showOrgColumn,
  showMirrorAcks,
}: {
  event: AuditRow
  showOrgColumn: boolean
  showMirrorAcks: boolean
}) {
  return (
    <div className="bg-background border-t border-border/40 px-5 pb-4 pt-4 space-y-5">
      <div className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 items-baseline">
        {showOrgColumn && (
          <Property label="organization" value={event.organization_slug ?? '-'} />
        )}
        <ActorRow event={event} />
        {event.ip && <Property label="ip" value={event.ip} mono />}
        {event.trace_id && (
          <Property label="trace id" value={event.trace_id} mono />
        )}
        {event.user_agent && (
          <Property label="user agent" value={event.user_agent} />
        )}
      </div>

      {Object.keys(event.metadata).length > 0 && (
        <MetadataBlock value={event.metadata} />
      )}

      {showMirrorAcks && <MirrorAcks acks={event.mirror_acks} />}

      <IntegrityFooter event={event} />
    </div>
  )
}

function Property({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <>
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>
      <span
        className={`min-w-0 text-foreground truncate ${mono ? 'font-mono text-[11px]' : 'text-xs'}`}
        title={value}
      >
        {value}
      </span>
    </>
  )
}

function ActorRow({ event }: { event: AuditRow }) {
  const primary = event.actor_email ?? event.actor_label ?? event.actor_id ?? '-'
  const secondary = event.actor_email && event.actor_id ? event.actor_id : null
  return (
    <>
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        actor
      </span>
      <span className="min-w-0">
        <span className="flex items-baseline gap-2 text-xs text-foreground" title={primary}>
          <span className="truncate">{primary}</span>
          <span className="font-mono text-[10px] text-muted-foreground/60 shrink-0">
            ({event.actor_type})
          </span>
        </span>
        {secondary && (
          <span
            className="block font-mono text-[10px] text-muted-foreground/60 truncate mt-0.5"
            title={secondary}
          >
            {secondary}
          </span>
        )}
      </span>
    </>
  )
}

function MetadataBlock({ value }: { value: Record<string, unknown> }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/85">
          Metadata
        </span>
        <span className="h-px flex-1 bg-gradient-to-r from-[var(--color-tertiary)]/30 via-border to-transparent" />
      </div>
      <div className="relative bg-popover border border-border rounded-md p-3 overflow-x-auto">
        <JsonView
          value={value}
          collapsed={2}
          displayDataTypes={false}
          shortenTextAfterLength={120}
          style={hawkJsonTheme}
        />
      </div>
    </div>
  )
}

const hawkJsonTheme: React.CSSProperties = {
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '11px',
  backgroundColor: 'transparent',
  ['--w-rjv-color' as keyof React.CSSProperties]: 'rgb(245 244 242 / 0.9)',
  ['--w-rjv-background-color' as keyof React.CSSProperties]: 'transparent',
  ['--w-rjv-line-color' as keyof React.CSSProperties]: 'rgb(255 255 255 / 0.06)',
  ['--w-rjv-arrow-color' as keyof React.CSSProperties]: 'rgb(245 244 242 / 0.45)',
  ['--w-rjv-info-color' as keyof React.CSSProperties]: 'rgb(138 138 138 / 0.7)',
  ['--w-rjv-update-color' as keyof React.CSSProperties]: '#FF4D00',
  ['--w-rjv-copied-color' as keyof React.CSSProperties]: 'rgb(138 138 138 / 0.7)',
  ['--w-rjv-copied-success-color' as keyof React.CSSProperties]: '#CCFF00',
  ['--w-rjv-key-string' as keyof React.CSSProperties]: '#048EFF',
  ['--w-rjv-curlybraces-color' as keyof React.CSSProperties]: 'rgb(245 244 242 / 0.55)',
  ['--w-rjv-brackets-color' as keyof React.CSSProperties]: 'rgb(245 244 242 / 0.55)',
  ['--w-rjv-colon-color' as keyof React.CSSProperties]: 'rgb(245 244 242 / 0.4)',
  ['--w-rjv-quotes-color' as keyof React.CSSProperties]: 'rgb(245 244 242 / 0.4)',
  ['--w-rjv-quotes-string-color' as keyof React.CSSProperties]: 'rgb(245 244 242 / 0.4)',
  ['--w-rjv-type-string-color' as keyof React.CSSProperties]: 'rgb(245 244 242 / 0.9)',
  ['--w-rjv-type-int-color' as keyof React.CSSProperties]: '#048EFF',
  ['--w-rjv-type-float-color' as keyof React.CSSProperties]: '#048EFF',
  ['--w-rjv-type-bigint-color' as keyof React.CSSProperties]: '#048EFF',
  ['--w-rjv-type-boolean-color' as keyof React.CSSProperties]: '#CCFF00',
  ['--w-rjv-type-null-color' as keyof React.CSSProperties]: 'rgb(138 138 138 / 0.7)',
  ['--w-rjv-type-undefined-color' as keyof React.CSSProperties]: 'rgb(138 138 138 / 0.7)',
  ['--w-rjv-type-nan-color' as keyof React.CSSProperties]: '#F43F5E',
  ['--w-rjv-ellipsis-color' as keyof React.CSSProperties]: '#FF4D00',
}

function MirrorAcks({ acks }: { acks: Record<string, unknown> }) {
  const entries = Object.entries(acks ?? {}).filter(([, v]) => v != null)
  if (entries.length === 0) return null

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/85">
          Mirror acks
        </span>
        <span className="h-px flex-1 bg-gradient-to-r from-emerald-500/30 via-border to-transparent" />
      </div>
      <div className="space-y-1.5">
        {entries.map(([mirrorId, raw]) => (
          <MirrorAckRow key={mirrorId} mirrorId={mirrorId} raw={raw} />
        ))}
      </div>
    </div>
  )
}

function MirrorAckRow({ mirrorId, raw }: { mirrorId: string; raw: unknown }) {
  const ack = raw as { acked_at?: string; external_ref?: unknown }
  const summary = summariseExternalRef(ack.external_ref)
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-2 py-0.5 text-emerald-300">
        <Database className="size-3" strokeWidth={1.75} />
        <span className="text-foreground">{mirrorId}</span>
      </span>
      {summary && (
        <span className="font-mono text-[11px] text-muted-foreground">
          {summary}
        </span>
      )}
      {ack.acked_at && (
        <span className="text-muted-foreground text-[11px]">
          {formatDate(ack.acked_at)}
        </span>
      )}
    </div>
  )
}

function summariseExternalRef(ref: unknown): string | null {
  if (!ref || typeof ref !== 'object') return null
  const r = ref as Record<string, unknown>
  if (typeof r.tx_id === 'number' || typeof r.tx_id === 'string') {
    return `tx ${r.tx_id}`
  }
  return null
}

function IntegrityFooter({ event }: { event: AuditRow }) {
  return (
    <div className="flex items-center gap-3 flex-wrap pt-3 border-t border-border/40 text-[10px] font-mono tracking-[0.18em] text-muted-foreground/70">
      <span
        className="inline-flex items-center gap-1.5 uppercase text-[var(--color-signal)]"
        title="This row is ed25519-signed and linked into the audit chain. Use the Verify integrity button to re-check the whole chain."
      >
        <ShieldCheck className="size-3" strokeWidth={1.75} />
        Signed
      </span>
      <CopyChip label="key" value={event.signing_key_id} display={event.signing_key_id} />
      <CopyChip label="row" value={event.row_hash} display={shortHash(event.row_hash)} />
      <CopyChip label="prev" value={event.prev_hash} display={shortHash(event.prev_hash)} />
      <CopyChip label="sig" value={event.signature} display={shortHash(event.signature)} />
    </div>
  )
}

function CopyChip({
  label,
  value,
  display,
}: {
  label: string
  value: string
  display: string
}) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
      className="inline-flex items-baseline gap-1 hover:text-foreground transition-colors group"
      title={`${value}\n(click to copy)`}
    >
      <span className="text-muted-foreground/40 lowercase">{label}</span>
      <span className="text-foreground/75 lowercase tracking-normal group-hover:text-foreground">
        {copied ? 'copied' : display}
      </span>
    </button>
  )
}

type ChainStatus = 'idle' | 'checking' | 'ok' | 'broken' | 'error'

const TONE_BY_STATUS: Record<ChainStatus, string> = {
  ok: 'text-[var(--color-signal)] ring-[var(--color-signal)]/30 hover:ring-[var(--color-signal)]/60',
  broken: 'text-destructive ring-destructive/40 hover:ring-destructive/60',
  error: 'text-destructive ring-destructive/40 hover:ring-destructive/60',
  idle: 'text-muted-foreground ring-border hover:ring-muted-foreground/40',
  checking: 'text-muted-foreground ring-border hover:ring-muted-foreground/40',
}

function ChainStatusPill({ verifyUrl, enabled }: { verifyUrl: string; enabled: boolean }) {
  const [status, setStatus] = useState<ChainStatus>('idle')
  const [result, setResult] = useState<AuditVerifyResult | null>(null)

  const runVerify = async () => {
    setStatus('checking')
    try {
      const r = await apiFetch(verifyUrl, { method: 'POST' })
      if (!r.ok) {
        setStatus('error')
        return
      }
      const parsed = (await r.json()) as AuditVerifyResult
      setResult(parsed)
      setStatus(parsed.ok ? 'ok' : 'broken')
    } catch {
      setStatus('error')
    }
  }

  useEffect(() => {
    if (enabled) {
      void runVerify()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, verifyUrl])

  const tone = TONE_BY_STATUS[status]
  const tooltip =
    status === 'broken'
      ? `Chain broken at #${result?.broken_at_seq ?? '?'}. Reason: ${result?.reason ?? 'unknown'}. Click to re-verify.`
      : status === 'error'
        ? 'Verify failed. Click to retry.'
        : status === 'ok'
          ? `Re-verified. ${result?.checked !== undefined ? `${result.checked} rows checked. ` : ''}Click to re-verify.`
          : 'Verifying...'

  return (
    <button
      type="button"
      onClick={runVerify}
      disabled={status === 'checking'}
      title={tooltip}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md ring-1 ring-inset bg-card font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${tone}`}
    >
      {status === 'checking' ? (
        <>
          <Loader2 className="size-3 animate-spin" strokeWidth={1.75} />
          verifying
        </>
      ) : status === 'ok' ? (
        <>
          <ShieldCheck className="size-3" strokeWidth={1.75} />
          chain verified
          {result?.checked !== undefined && (
            <span className="text-muted-foreground/60 normal-case tracking-normal ml-1">
              / {result.checked} rows
            </span>
          )}
        </>
      ) : status === 'broken' ? (
        <>
          <ShieldAlert className="size-3" strokeWidth={1.75} />
          chain broken
          {result?.broken_at_seq !== null && result?.broken_at_seq !== undefined && (
            <span className="normal-case tracking-normal ml-1">at #{result.broken_at_seq}</span>
          )}
        </>
      ) : status === 'error' ? (
        <>
          <ShieldAlert className="size-3" strokeWidth={1.75} />
          verify failed
        </>
      ) : (
        <>
          <Shield className="size-3" strokeWidth={1.75} />
          not verified
        </>
      )}
    </button>
  )
}

function shortId(s: string): string {
  if (s.length <= 12) return s
  return `${s.slice(0, 8)}...`
}

function shortHash(s: string): string {
  if (s === 'genesis') return s
  if (s.length <= 14) return s
  return `${s.slice(0, 10)}...${s.slice(-4)}`
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
